import CustomInputField from "@components/FormFields/CustomInputField";
import { CircularProgressIndicator } from "@components/shared/CircularProgressIndicator";
import CustomHead from "@components/shared/CustomHead";
import SignInPageContainer from "@components/shared/SignInPageContainer";
import { useAuth } from "@hooks/useAuth";
import useDialog from "@hooks/useDialog";
import useGzLog from "@hooks/useGzLog";
import { supabase } from "@utils/serverClient";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { MdArrowForward } from "react-icons/md";
import { RiBuilding4Line } from "react-icons/ri";
import { TbPaint } from "react-icons/tb";
import MutationFetchStatus from "types/MutationFetchStatus";

const _steps = ["Create Organization", "🚀 Liftoff"];

function CompleteSignUpPage() {
  const [step, setStep] = useState(0);

  return (
    <div>
      <CustomHead title="Complete Signup" />

      <SignInPageContainer>
        <div className="flex flex-col items-center justify-center">
          <div className="flex">
            {_steps.map((val, index) => (
              <div key={index} className="flex items-baseline">
                {index !== 0 && (
                  <div className="h-2 w-16 rounded-r-full bg-white">
                    <div
                      className={`${index <= step ? "w-full" : "w-0"
                        } h-full rounded-r-full bg-stepperColor transition-all
                          `}
                    />
                  </div>
                )}

                <div className="mx-2 mb-8 inline-flex h-10 w-10 items-center justify-center rounded-full bg-stepperColor p-2 font-semibold text-white">
                  {index + 1}
                </div>

                {index !== _steps.length - 1 && (
                  <div className="h-2 w-16 rounded-l-full bg-white">
                    <div
                      className={`${index <= step ? "w-full" : "w-0"
                        } h-full bg-stepperColor transition-all ${step === index ? "rounded-full" : "rounded-l-full"
                        }`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex w-auto items-center justify-center rounded-3xl bg-white p-16 text-black backdrop-blur-md ">
            <div className="flex w-96 max-w-md flex-col items-center gap-8">
              <div className="h-96 w-full rounded-md p-8 dark:border-darkBorderColor">
                {step == 0 ? (
                  <CreateOrganization
                    onSuccess={() => {
                      setStep(step + 1);
                    }}
                  />
                ) : (
                  <LiftOff />
                )}
              </div>
            </div>
          </div>
        </div>
      </SignInPageContainer>
    </div>
  );
}

interface CreateOrgFormData {
  orgName: string;
}

function CreateOrganization({ onSuccess }: { onSuccess: () => void }) {
  const { log: gzLog } = useGzLog()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateOrgFormData>();

  const { user, profileUpdateStatus, updateProfile } = useAuth();

  const [organizationCreateStatus, setOrganizationCreateStatus] =
    useState<MutationFetchStatus>("idle");

  const { showErrorSnackbar } = useDialog();

  const onSubmit = async (formData: CreateOrgFormData) => {
    setOrganizationCreateStatus("pending");

    if (!user) {
      setOrganizationCreateStatus("error");
      showErrorSnackbar("Error creating organization");
      return;
    }

    const { error, data: org } = await supabase
      .from("organization")
      .insert({ name: formData.orgName, created_by: user?.id })
      .select("id")
      .single()

    if (error || !org) {
      setOrganizationCreateStatus("error");
      showErrorSnackbar("Error creating organization");
      gzLog({
        eventtype: "OrganizationCreationFailed",
        name: "OrganizationCreationFailed",
        payload: {
          organizationName: formData.orgName,
          creatorUserId: user?.id,
          creatorUserEmail: user?.email ?? "undefined",
          errorMessage: String(error?.message || "Unknown error creating organization"),
        },
      });
      return;
    }

    updateProfile({
      updatedUser: ({
        ...user,
        org_id: org.id,
        can_create_own_sub: true,
        canCreateOwnSub: true,
      }),
      notify: false,
    }, {
      onError() {
        setOrganizationCreateStatus("error");
        showErrorSnackbar("Error updating profile");
      },
      async onSuccess() {
        const { error: profileOrgError } = await supabase
          .from("profile_organization")
          .insert({ user_id: user.id, org_id: org.id, invitation_status: "accepted" })

        if (profileOrgError) {
          setOrganizationCreateStatus("error");
          showErrorSnackbar("Error creating organization");
          return;
        }

        if (user) {
          setOrganizationCreateStatus("success");
          onSuccess();
          gzLog({
            eventtype: "OrganizationCreated",
            name: "OrganizationCreated",
            payload: {
              organizationId: org.id,
              organizationName: formData.orgName,
              creatorUserId: user.id,
              creatorUserEmail: user.email ?? "undefined",
            },
          });
        } else {
          setOrganizationCreateStatus("error");
          showErrorSnackbar("Error creating organization");
          gzLog({
            eventtype: "OrganizationCreationFailed",
            name: "OrganizationCreationFailed",
            payload: {
              organizationName: formData.orgName,
              errorMessage: String("User not found after profile update"),
            },
          });
        }
      },
    });
  };

  return (
    <form
      key="complete_signup_form"
      onSubmit={handleSubmit(onSubmit)}
      className=" flex h-full flex-col items-center justify-center gap-8"
    >
      <h1 className="text-3xl font-semibold">Create Organization</h1>
      <h2>{`Let's create your Organization!`}</h2>
      <div className="w-full">
        <CustomInputField
          leadingIcon={<RiBuilding4Line />}
          enableDarkMode={false}
          placeholder="Enter your Organization name"
          {...register("orgName", { required: true })}
        />

        {errors.orgName && (
          <span className="input-field-error">This field is required</span>
        )}
      </div>
      <button
        className="primary_button"
        type="submit"
        disabled={
          profileUpdateStatus === "pending" ||
          organizationCreateStatus === "pending"
        }
      >
        Continue
        {profileUpdateStatus === "pending" ||
          organizationCreateStatus === "pending" ? (
          <CircularProgressIndicator />
        ) : (
          <MdArrowForward />
        )}
      </button>
    </form>
  );
}

function LiftOff() {
  const [isWide, setIsWide] = useState(false);
  const _initialDelay = 100;
  const _duration = 8000;
  const { getProfile } = useAuth()
  const router = useRouter()

  useEffect(() => {
    setIsWide(false);
    setTimeout(() => {
      setIsWide(true);
      setTimeout(() => {
        getProfile()
      }, _duration);
    }, _initialDelay);
  }, []);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8">
      <IoCheckmarkCircleOutline className="text-6xl text-gray-400" />
      <h1 className="text-3xl font-semibold">{`Congratulations!`}</h1>
      <h2>Successfully Signed Up</h2>
      <button
        key={"start_designing_button"}
        onClick={() => {
          getProfile()
          router.push("/")
        }}
        className="primary_button relative overflow-hidden"
      >
        <div
          className={`${isWide ? "w-full" : "w-0"
            } absolute left-0 h-full bg-buttonColor bg-opacity-10 transition-all ease-in-out`}
          style={{
            ///TODO: rounded at the start and end and just straight at the middle
            transitionDuration: _duration + "ms",
            transitionDelay: _initialDelay + "ms",
          }}
        ></div>
        <TbPaint className="z-10" />
        <div className="z-10">Start Designing</div>
      </button>
    </div>
  );
}
export default CompleteSignUpPage;
